import {
  ArrowLeftOutlined,
  DownloadOutlined,
  PlusOutlined,
  WarningFilled,
} from '@ant-design/icons';
import { useDataQualityTestCRUDActions } from '@mm/state/actions/DataQualityTestCRUDActions';
import { Collapse, CollapseProps, ConfigProvider, Divider, Spin } from 'antd';
import classNames from 'classnames';
import { uniqueId } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { BookDemoButton } from '../../../common/buttons/BookDemoButton/BookDemoButton';
import { Logo } from '../../../layout/BotBuilderSidebar/Logo/Logo';
import { OPENSEND_FAQ, RETENTION_FAQ } from './constants';
import styles from './DataQualityResultPage.module.css';
import { useDataQualityResults } from './use-data-quality-results';

export const DataQualityResultPage = () => {
  const {
    maxMismatch,
    calcMatchRateRatio,
    calcBarHeight,
    createPercentageArray,
    calcMatchRate,
    calcAccuracy,
    BAR_HEIGHT,
    LINE_HEIGHT,
    results,
  } = useDataQualityResults();

  const { getResult } = useDataQualityTestCRUDActions();
  const history = useHistory();

  const [busy, setBusy] = useState(false);
  const facebookPageId = window.location.pathname.split('/').pop();
  const url = results?.websiteUrl;

  const CALENDLY_URL =
    'https://calendly.com/d/cmtp-44s-vqw/customers-ai-data-quality-review';

  const mappedSources = results?.sources?.map(res => ({
    ...res,
    matchedHeightValueInPx:
      (BAR_HEIGHT * calcAccuracy(res.matched, res.mismatched)) / 100,
  }));

  const xraySource = mappedSources?.find(s => s.source === 'xray');
  const xRayAccuracy = calcAccuracy(
    xraySource?.matched,
    xraySource?.mismatched,
  );

  const competitors = mappedSources?.filter(c => c.source !== 'xray');
  const getItems = (source: string) => {
    const faq = source === 'opensend' ? OPENSEND_FAQ : RETENTION_FAQ;
    const items: CollapseProps['items'] = faq.map(i => ({
      ...i,
      key: uniqueId(),
      showArrow: false,
      extra: <PlusOutlined />,
    }));

    return items;
  };

  const percentages = Array.from({ length: 11 }, (_, i) => i * 10).sort(
    (a, b) => b - a,
  );

  const manyCompetitors = (competitors?.length || 0) > 1;
  const noCompetitors = competitors?.length === 0;

  const percentagesArrayMatchRate = createPercentageArray();

  const xRayTruMatchBarHeight = calcBarHeight(
    percentagesArrayMatchRate[0],
    calcMatchRate(xraySource?.matched),
  );

  const renderCompetitorNames = (
    sourceClassName?: string,
    textClassName?: string,
    delimeter = 'vs',
  ) =>
    competitors?.map((competitor, idx) => (
      <React.Fragment key={competitor.source}>
        <span className={sourceClassName}>{competitor.source}</span>
        <span className={textClassName}>
          .com {idx !== competitors.length - 1 && delimeter}
        </span>{' '}
      </React.Fragment>
    ));

  const fetchResult = async () => {
    if (facebookPageId) {
      setBusy(true);
      await getResult(Number(facebookPageId));
      setBusy(false);
    }
  };

  const onEventScheduled = () => {
    setTimeout(() => history.goBack(), 3000);
  };

  useEffect(() => {
    fetchResult();
  }, []);

  return (
    <Spin spinning={busy}>
      <div className={styles.container}>
        <div className={styles.header}>
          <div
            className={styles.logoWithBackBtn}
            onClick={() => history.goBack()}
          >
            <ArrowLeftOutlined />
            <Logo />
          </div>

          <div className={styles.downloadButton}>
            <DownloadOutlined className={styles.downloadIcon} />
            <BookDemoButton
              show
              className={styles.transparentButton}
              button="Get Full Report"
              onEventScheduled={onEventScheduled}
              calendlyUrl={CALENDLY_URL}
            />
          </div>
        </div>
        {!results?.success && !busy && (
          <div className={styles.emptyResults}>
            To get results contact us at{' '}
            <a
              className={styles.supportLink}
              href="mailto:sales@customers.ai"
              rel="noopener noreferrer"
              target="_blank"
            >
              {' '}
              sales@customers.ai
            </a>
          </div>
        )}
        {results?.success && (
          <>
            <div className={styles.titleContainer}>
              <div className={styles.block}>
                <div className={styles.website}>{url}</div>
                <h1 className={styles.title}>Data Quality Test Report </h1>
              </div>
              {!noCompetitors && (
                <div className={classNames(styles.alert, styles.block)}>
                  <div
                    className={classNames(
                      styles.message,
                      styles.redBg,
                      styles.white,
                      styles.textPoppins,
                    )}
                  >
                    <WarningFilled className={styles.warningIcon} />
                    Damage to your ESP
                  </div>
                  {competitors?.map(competitor => (
                    <div
                      className={classNames(styles.message)}
                      key={competitor.source}
                    >
                      <WarningFilled
                        className={classNames(
                          styles.warningIcon,
                          styles.font20,
                          styles.redcolor,
                        )}
                      />
                      <span>
                        <span style={{ textTransform: 'capitalize' }}>
                          {competitor?.source}
                        </span>{' '}
                        is identifying{' '}
                        <strong>
                          {(
                            100 -
                            calcAccuracy(
                              competitor.matched,
                              competitor.mismatched,
                            )
                          ).toFixed(2)}
                          % of wrong emails.
                        </strong>
                      </span>
                    </div>
                  ))}

                  <div className={classNames(styles.message)}>
                    <WarningFilled
                      className={classNames(
                        styles.warningIcon,
                        styles.font20,
                        styles.redcolor,
                      )}
                    />
                    <span>
                      <strong>{maxMismatch}% less emails landing</strong> in the
                      correct inbox.
                    </span>
                  </div>
                </div>
              )}
            </div>
            <div
              className={classNames(styles.charts, {
                [styles.wideCharts]: manyCompetitors,
              })}
            >
              <div className={styles.content}>
                <div className={styles.contentHeader}>Data Accuracy</div>
                <div className={styles.title} style={{ fontWeight: 700 }}>
                  Part 1.
                </div>
                <div className={styles.title}>
                  Comparing <span className={styles.website}>{url}</span> actual
                  form fill data vs. predicted identities from{' '}
                  <span className={styles.website}>Customers.ai</span> vs{' '}
                  {renderCompetitorNames(
                    styles.capitalized,
                    styles.font400,
                    'vs',
                  )}
                </div>
                <div
                  className={classNames(styles.chart, {
                    [styles.centered]: manyCompetitors,
                  })}
                >
                  <div
                    className={classNames(styles.linesBg, {
                      [styles.linesBgNarrow]: noCompetitors,
                      [styles.linesBgWide]: manyCompetitors,
                    })}
                  >
                    {percentages.map(v => (
                      <Divider
                        orientation="left"
                        plain
                        key={v}
                        className={styles.divider}
                      >
                        {v}%
                      </Divider>
                    ))}
                  </div>
                  <div className={styles.bars}>
                    <div className={classNames(styles.barWrapper)}>
                      <Logo className={styles.logo} />
                      <div className={styles.barNote}>
                        Customers.ai made{' '}
                        <span style={{ fontWeight: 700 }}>
                          {(xraySource?.matched || 0) +
                            (xraySource?.mismatched || 0)}{' '}
                          “predictions”
                        </span>{' '}
                        of which {xraySource?.matched} overlapped with{' '}
                        {results.total} verified customers and{' '}
                        <span style={{ fontWeight: 700 }}>
                          had an accuracy of {xRayAccuracy}%
                        </span>
                      </div>

                      <div
                        className={styles.bar}
                        style={{ height: BAR_HEIGHT }}
                      >
                        <div
                          className={styles.greenBar}
                          style={{
                            height: xraySource?.matchedHeightValueInPx,
                          }}
                        >
                          {' '}
                          <span className={styles.percent}>
                            {xRayAccuracy}%
                          </span>
                        </div>
                        <div className={styles.redBar}>
                          <span className={styles.percent}>
                            {(100 - xRayAccuracy).toFixed(2)}%
                          </span>
                        </div>
                      </div>
                    </div>
                    {competitors?.map(competitor => {
                      const matched = calcAccuracy(
                        competitor?.matched,
                        competitor?.mismatched,
                      );
                      const mismatched = (100 - matched).toFixed(2);

                      return (
                        <div
                          className={classNames(
                            styles.barWrapper,
                            styles.grayBg,
                          )}
                          key={competitor.source}
                        >
                          <div className={styles.textPoppins}>
                            <span style={{ textTransform: 'capitalize' }}>
                              {competitor.source}
                            </span>
                            .com
                          </div>
                          <div className={styles.barNote}>
                            <span style={{ textTransform: 'capitalize' }}>
                              {competitor.source}
                            </span>{' '}
                            made{' '}
                            <span style={{ fontWeight: 700 }}>
                              {(competitor?.matched || 0) +
                                (competitor?.mismatched || 0)}{' '}
                              “predictions”
                            </span>{' '}
                            of which {competitor?.matched} overlapped with{' '}
                            {results.total} verified customers and{' '}
                            <span style={{ fontWeight: 700 }}>
                              had an accuracy of{' '}
                              {calcAccuracy(
                                competitor?.matched,
                                competitor?.mismatched,
                              )}
                              %
                            </span>
                          </div>
                          <div
                            className={styles.bar}
                            style={{ height: BAR_HEIGHT }}
                          >
                            {matched > 0 && (
                              <div
                                className={styles.greenBar}
                                style={{
                                  height: competitor?.matchedHeightValueInPx,
                                }}
                              >
                                <span className={styles.percent}>
                                  {matched}%
                                </span>
                              </div>
                            )}

                            <div className={styles.redBar}>
                              <span className={styles.percent}>
                                {mismatched}%
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className={styles.legend}>
                  <div className={styles.legendItem}>
                    <span className={styles.rectangle} />
                    <span className={styles.legendText}>Exact match</span>
                  </div>
                  <div className={styles.legendItem}>
                    <span
                      className={classNames(styles.rectangle, styles.redBg)}
                    />
                    <span className={styles.legendText}>Incorrect match</span>
                  </div>
                </div>
              </div>
              <div className={classNames(styles.content, styles.trueMatchRate)}>
                <div>
                  <div className={styles.contentHeader}>True Match Rate</div>
                  <div className={styles.title} style={{ fontWeight: 700 }}>
                    Part 2.
                  </div>
                  <div className={styles.title}>
                    Comparing <span className={styles.website}>{url}</span> % of
                    customers that were identified from{' '}
                    <span className={styles.website}>Customers.ai</span> vs{' '}
                    {competitors?.map((competitor, idx) => (
                      <React.Fragment key={competitor.source}>
                        <span
                          style={{
                            fontWeight: 400,
                            textTransform: 'capitalize',
                          }}
                        >
                          {competitor.source}
                        </span>
                        <span style={{ fontWeight: 400 }}>
                          .com {idx !== competitors.length - 1 && 'vs'}
                        </span>{' '}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
                <div
                  className={classNames(styles.chart, {
                    [styles.centered]: manyCompetitors,
                  })}
                >
                  <div
                    className={classNames(styles.linesBg, {
                      [styles.linesBgNarrow]: noCompetitors,
                      [styles.linesBgWide]: (competitors?.length || 0) > 1,
                    })}
                  >
                    {percentagesArrayMatchRate.map(v => (
                      <Divider
                        orientation="left"
                        plain
                        key={v}
                        className={styles.divider}
                      >
                        {v}%
                      </Divider>
                    ))}
                  </div>
                  <div className={styles.bars}>
                    <div className={classNames(styles.barWrapper)}>
                      <div className={styles.barHeader}>
                        <Logo className={styles.logo} />
                        <div
                          className={classNames(styles.barNote, {
                            [styles.highBarNote]: !manyCompetitors,
                          })}
                        >
                          Customers.ai correctly identified{' '}
                          <span style={{ fontWeight: 700 }}>
                            {xraySource?.matched} of the {results.total}
                          </span>{' '}
                          verified customers
                        </div>
                      </div>
                      <div
                        className={styles.bar}
                        style={{
                          height: xRayTruMatchBarHeight,
                        }}
                      >
                        <div
                          className={styles.greenBar}
                          style={{
                            height: xRayTruMatchBarHeight,
                          }}
                        >
                          {' '}
                          <span className={styles.percent}>
                            {calcMatchRate(xraySource?.matched)}%
                          </span>
                        </div>
                      </div>
                    </div>
                    {competitors?.map(competitor => {
                      const matched = calcMatchRate(competitor?.matched);
                      const minBarHeight = calcBarHeight(
                        percentagesArrayMatchRate[0],
                        matched,
                      );

                      return (
                        <div
                          className={classNames(
                            styles.barWrapper,
                            styles.grayBg,
                          )}
                          key={competitor.source}
                        >
                          <div className={styles.barHeader}>
                            <div className={styles.textPoppins}>
                              <span style={{ textTransform: 'capitalize' }}>
                                {competitor.source}
                              </span>
                              .com
                            </div>
                            <div
                              className={classNames(styles.barNote, {
                                [styles.highBarNote]: !manyCompetitors,
                              })}
                            >
                              <span style={{ textTransform: 'capitalize' }}>
                                {competitor.source}
                              </span>
                              .com correctly identified{' '}
                              <span style={{ fontWeight: 700 }}>
                                {competitor?.matched} of the {results.total}
                              </span>{' '}
                              verified customers
                            </div>
                          </div>
                          <div
                            className={styles.bar}
                            style={{
                              height:
                                minBarHeight > LINE_HEIGHT
                                  ? minBarHeight
                                  : LINE_HEIGHT,
                            }}
                          >
                            <div className={styles.redBar}>
                              <span className={styles.percent}>{matched}%</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className={styles.legend}>
                  <div className={styles.legendItem}>
                    <span className={styles.rectangle} />
                    <span className={styles.legendText}>
                      Customers.ai True match rate
                    </span>
                  </div>
                  {!noCompetitors && (
                    <div className={styles.legendItem}>
                      <span
                        className={classNames(styles.rectangle, styles.redBg)}
                      />

                      <span className={styles.legendText}>
                        {renderCompetitorNames(
                          styles.capitalized,
                          styles.font500,
                          'and',
                        )}{' '}
                        True match rate
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div
              className={styles.block}
              style={{ alignItems: 'center', gap: 16, marginBottom: 80 }}
            >
              {competitors?.map(competitor => (
                <div className={styles.alert} key={competitor.source}>
                  <div
                    className={classNames(
                      styles.message,
                      styles.redBg,
                      styles.white,
                      styles.textPoppins,
                    )}
                  >
                    <WarningFilled className={styles.warningIcon} />
                    Alert
                  </div>
                  <div className={styles.textPoppins}>
                    <span style={{ textTransform: 'capitalize' }}>
                      {competitor.source}
                    </span>
                    .com had a TRUE match rate that was{' '}
                    {calcMatchRateRatio(
                      xraySource?.matched || 0,
                      competitor.matched,
                    )}
                    X lower
                  </div>
                </div>
              ))}

              {!noCompetitors && (
                <div className={styles.alert}>
                  <div
                    className={classNames(
                      styles.message,
                      styles.redBg,
                      styles.white,
                      styles.textPoppins,
                    )}
                  >
                    <WarningFilled className={styles.warningIcon} />
                    Alert
                  </div>
                  {Number(maxMismatch) > 0 && (
                    <div className={styles.textPoppins}>
                      Over{' '}
                      <span className={styles.redcolor}>
                        {maxMismatch}% of the emails
                      </span>{' '}
                      identified by competitor were{' '}
                      <span className={styles.redcolor}>incorrect</span>
                    </div>
                  )}
                </div>
              )}

              <BookDemoButton
                show
                className={styles.button}
                onEventScheduled={onEventScheduled}
                button="Get Full Damage Report"
                calendlyUrl={CALENDLY_URL}
              />
            </div>

            {competitors?.map(c => (
              <div className={styles.faq} key={c.source}>
                <h3 className={styles.title}>
                  Customers.ai vs{' '}
                  <span style={{ textTransform: 'capitalize' }}>
                    {c.source}
                  </span>
                  .com FAQs
                </h3>
                <ConfigProvider
                  theme={{
                    components: {
                      Collapse: {
                        fontSize: 17,
                        headerBg: 'white',
                      },
                    },
                  }}
                >
                  <Collapse
                    items={getItems(c.source)}
                    className={styles.collapse}
                  />
                </ConfigProvider>
              </div>
            ))}
          </>
        )}
      </div>
    </Spin>
  );
};
